import { useState, useMemo } from 'react';
import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import './PreviewTable.css';

export interface ColumnDef<T> {
  key: keyof T;
  header: string;
  sortable?: boolean;
  render?: (value: T[keyof T], row: T) => React.ReactNode;
  width?: string;
}

interface PreviewTableProps<T> {
  data: T[];
  columns: ColumnDef<T>[];
  selectedIds: Set<number>;
  onSelectionChange: (ids: Set<number>) => void;
  idField: keyof T;
  emptyMessage?: string;
}

type SortDir = 'asc' | 'desc';

export default function PreviewTable<T>({
  data,
  columns,
  selectedIds,
  onSelectionChange,
  idField,
  emptyMessage = 'No data to display',
}: Readonly<PreviewTableProps<T>>) {
  const [sortKey, setSortKey] = useState<keyof T | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const sortedData = useMemo(() => {
    if (!sortKey) return data;
    return [...data].sort((a, b) => {
      const aVal = a[sortKey];
      const bVal = b[sortKey];
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      const cmp = String(aVal).localeCompare(String(bVal), undefined, { numeric: true, sensitivity: 'base' });
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [data, sortKey, sortDir]);

  const allSelected = data.length > 0 && data.every((row) => selectedIds.has(row[idField] as number));
  const someSelected = data.some((row) => selectedIds.has(row[idField] as number));

  const handleSelectAll = () => {
    const next = new Set(selectedIds);
    if (allSelected) {
      // Deselect only this page's rows
      for (const row of data) {
        next.delete(row[idField] as number);
      }
    } else {
      // Select this page's rows (preserving selections from other pages)
      for (const row of data) {
        next.add(row[idField] as number);
      }
    }
    onSelectionChange(next);
  };

  const handleSelectRow = (id: number) => {
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    onSelectionChange(next);
  };

  const handleSort = (key: keyof T) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  if (data.length === 0) {
    return (
      <div className="pvt-empty">
        <p>{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="pvt-wrapper">
      <table className="pvt-table">
        <thead>
          <tr>
            <th className="pvt-checkbox-col">
              <input
                type="checkbox"
                checked={allSelected}
                ref={(el) => {
                  if (el) el.indeterminate = someSelected && !allSelected;
                }}
                onChange={handleSelectAll}
                aria-label="Select all rows"
              />
            </th>
            {columns.map((col) => (
              <th
                key={String(col.key)}
                style={col.width ? { width: col.width } : undefined}
                className={col.sortable ? 'pvt-sortable' : ''}
                onClick={col.sortable ? () => handleSort(col.key) : undefined}
              >
                <span className="pvt-header-content">
                  {col.header}
                  {col.sortable && (
                    <span className="pvt-sort-icon">
                      {sortKey === col.key ? (
                        sortDir === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />
                      ) : (
                        <ArrowUpDown size={12} />
                      )}
                    </span>
                  )}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedData.map((row) => {
            const rowId = row[idField] as number;
            const isSelected = selectedIds.has(rowId);
            return (
              <tr key={rowId} className={isSelected ? 'pvt-row-selected' : ''}>
                <td className="pvt-checkbox-col">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleSelectRow(rowId)}
                    aria-label={`Select row ${rowId}`}
                  />
                </td>
                {columns.map((col) => (
                  <td key={String(col.key)}>
                    {col.render ? col.render(row[col.key], row) : String(row[col.key] ?? '')}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="pvt-footer">
        {selectedIds.size} selected total &middot; {data.filter((row) => selectedIds.has(row[idField] as number)).length} on this page
      </div>
    </div>
  );
}